// WS5 — Headbox privacy advanced viewer-side mirror.
//
// Speculare di oniri-viewer-api/src/auth/headbox-privacy.ts. Quando il manifest
// di una scena privata via share_link arriva con `headbox_privacy:
// HEADBOX_PRIVACY_VERSION`, il viewer chiama `unapplyHeadboxOffset(token, index)`
// per reverse-applicare l'offset a `headbox.center` e `default_camera.pos`
// prima di costruire AABB / fit camera.
//
// Consumer raccomandato: `normalizeManifestViewboxes(manifest, shareToken)`
// che gestisce il pass-through (manifest pubblico raw) vs decrypt (share_link
// privato) in modo trasparente. Usato da PR3 orchestrator wiring.
//
// Trade-off (cfr. PLAN_WS5 §6.4 + backend headbox-privacy.ts):
//  - Reversibile da chi possiede il share_token (= chi ha il link). NON e'
//    end-to-end encryption.
//  - Difesa "depth in defense" vs cache pubblica/CDN, browser cache, network
//    log accidentali, screenshot DevTools.
//
// Algoritmo (deve restare 1:1 col backend, qualsiasi drift rompe il
// round-trip):
//  - HKDF-SHA256(share_token, salt='oniri-ws5-headbox-offset-v1', info='headbox-offset')
//    → 32 byte di key material.
//  - PRF = HMAC-SHA256(key, viewbox_index_BE_uint32) → 32 byte.
//  - 3 uint32 BE estratti dai primi 12 byte → [-1, +1] uniforme → [-5m, +5m].

import type { ModelManifest, ModelViewbox, Vec3 } from "./types.js";

/**
 * Versione del formato privacy. Mirror della costante backend
 * (HEADBOX_PRIVACY_VERSION in oniri-viewer-api/src/auth/headbox-privacy.ts).
 * Per upgrade futuri, bumpare a 'offset_hmac_v2' SIMULTANEAMENTE in entrambi
 * i repo + mantenere parallel decode in entrambi per la finestra di overlap.
 */
export const HEADBOX_PRIVACY_VERSION = "offset_hmac_v1" as const;
export type HeadboxPrivacyVersion = typeof HEADBOX_PRIVACY_VERSION;

const RANGE_M = 5.0;

const HKDF_SALT = new TextEncoder().encode("oniri-ws5-headbox-offset-v1");
const HKDF_INFO = new TextEncoder().encode("headbox-offset");

async function deriveHeadboxKey(shareToken: string): Promise<ArrayBuffer> {
	const ikm = new TextEncoder().encode(shareToken);
	const key = await crypto.subtle.importKey(
		"raw",
		ikm,
		{ name: "HKDF" },
		false,
		["deriveBits"],
	);
	return crypto.subtle.deriveBits(
		{
			name: "HKDF",
			hash: "SHA-256",
			salt: HKDF_SALT,
			info: HKDF_INFO,
		},
		key,
		256,
	);
}

/** Calcola offset (dx, dy, dz) in metri per (shareToken, viewboxIndex). */
export async function headboxOffsetXYZ(
	shareToken: string,
	viewboxIndex: number,
): Promise<readonly [number, number, number]> {
	const keyBytes = await deriveHeadboxKey(shareToken);
	const hmacKey = await crypto.subtle.importKey(
		"raw",
		keyBytes,
		{ name: "HMAC", hash: "SHA-256" },
		false,
		["sign"],
	);
	const idxBuf = new ArrayBuffer(4);
	new DataView(idxBuf).setUint32(0, viewboxIndex >>> 0, false);
	const sig = await crypto.subtle.sign("HMAC", hmacKey, idxBuf);
	const dv = new DataView(sig);
	const u0 = dv.getUint32(0, false);
	const u1 = dv.getUint32(4, false);
	const u2 = dv.getUint32(8, false);
	const MAX_U32 = 0xffffffff;
	const toSigned = (u: number): number => (u / MAX_U32) * 2 - 1;
	return [
		toSigned(u0) * RANGE_M,
		toSigned(u1) * RANGE_M,
		toSigned(u2) * RANGE_M,
	];
}

/** Reverse offset → coordinate raw. Mirror di applyHeadboxOffset backend. */
export async function unapplyHeadboxOffset(
	offsetCenter: readonly [number, number, number],
	shareToken: string,
	viewboxIndex: number,
): Promise<readonly [number, number, number]> {
	const [dx, dy, dz] = await headboxOffsetXYZ(shareToken, viewboxIndex);
	return [offsetCenter[0] - dx, offsetCenter[1] - dy, offsetCenter[2] - dz];
}

/**
 * Normalizza un ModelManifest che potrebbe avere coordinate viewbox offsetate.
 * Helper-consumer per PR3 orchestrator wiring: chiamato UNA volta dopo il
 * fetch manifest, gestisce 3 casi in modo trasparente:
 *
 *  1. `headbox_privacy` assente (manifest pubblico raw): pass-through.
 *  2. `headbox_privacy === HEADBOX_PRIVACY_VERSION` + shareToken fornito:
 *     decrypt headbox.center, default_camera.pos, ricalcola min/max.
 *  3. `headbox_privacy === HEADBOX_PRIVACY_VERSION` + shareToken NULL:
 *     errore di chiamata (il viewer ha ricevuto offset ma non ha il token →
 *     bug di plumbing, fail-loud per evitare coordinate corrotte).
 *  4. `headbox_privacy` valore SCONOSCIUTO: fail-loud (probabile manifest
 *     da backend con versione piu' nuova del client; richiede update viewer).
 *
 * Codex PR1 review C2 + I3: helper centralizzato che il PR3 chiama dopo
 * fetchModelManifest, no scattered decrypt nei consumer downstream.
 */
export async function normalizeManifestViewboxes(
	manifest: ModelManifest,
	shareToken: string | null,
): Promise<ModelManifest> {
	const privacy = manifest.headbox_privacy;
	if (privacy === undefined) return manifest;
	if (privacy !== HEADBOX_PRIVACY_VERSION) {
		throw new Error(
			`Manifest headbox_privacy='${String(privacy)}' sconosciuto al viewer ` +
				`(richiesto: ${HEADBOX_PRIVACY_VERSION}). Aggiorna il client.`,
		);
	}
	if (shareToken === null) {
		throw new Error(
			"Manifest ha headbox_privacy ma share_token non fornito. " +
				"Bug di plumbing: usare share_token dall'URL della richiesta share.",
		);
	}
	const viewboxes = manifest.viewboxes;
	if (viewboxes === undefined || viewboxes.length === 0) {
		// Niente da decrittare → strip flag.
		const out: ModelManifest = { ...manifest };
		delete out.headbox_privacy;
		return out;
	}
	const normalizedViewboxes: ModelViewbox[] = await Promise.all(
		viewboxes.map(async (vb) => decryptViewbox(vb, shareToken, vb.index)),
	);
	const out: ModelManifest = { ...manifest, viewboxes: normalizedViewboxes };
	delete out.headbox_privacy;
	return out;
}

async function decryptViewbox(
	vb: ModelViewbox,
	shareToken: string,
	viewboxIndex: number,
): Promise<ModelViewbox> {
	const rawCenter = await unapplyHeadboxOffset(
		vb.headbox.center,
		shareToken,
		viewboxIndex,
	);
	const rawPos = await unapplyHeadboxOffset(
		vb.default_camera.pos,
		shareToken,
		viewboxIndex,
	);
	const center: Vec3 = [rawCenter[0], rawCenter[1], rawCenter[2]];
	const size = vb.headbox.size; // invariante per traslazione
	const min: Vec3 = [
		center[0] - size[0] / 2,
		center[1] - size[1] / 2,
		center[2] - size[2] / 2,
	];
	const max: Vec3 = [
		center[0] + size[0] / 2,
		center[1] + size[1] / 2,
		center[2] + size[2] / 2,
	];
	return {
		...vb,
		headbox: { center, size, min, max },
		default_camera: {
			...vb.default_camera,
			pos: [rawPos[0], rawPos[1], rawPos[2]],
		},
	};
}
